import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { FiX, FiAlertCircle } from 'react-icons/fi';
import { Button } from '@/components/common';

interface DisclaimerModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.lg};
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.xl};
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  flex: 1;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.warning}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: ${theme.colors.warning};
  }
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  color: ${theme.colors.text}60;
  transition: all ${theme.transitions.fast};
  border-radius: ${theme.borderRadius.md};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }

  svg {
    font-size: 20px;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const SectionContent = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}90;
  line-height: 1.6;
  margin: 0;
`;

const List = styled.ul`
  margin: ${theme.spacing.sm} 0 0 ${theme.spacing.lg};
  padding: 0;

  li {
    font-size: ${theme.typography.sizes.sm};
    color: ${theme.colors.text}90;
    line-height: 1.6;
    margin-bottom: ${theme.spacing.sm};
  }
`;

const HighlightBox = styled.div`
  background: ${theme.colors.primary}10;
  border-left: 3px solid ${theme.colors.primary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin: ${theme.spacing.md} 0;
`;

const HighlightText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  margin: 0;
  font-weight: ${theme.typography.weights.medium};
`;

const ModalFooter = styled.div`
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: white;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column-reverse;
  }
`;

export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({
  isOpen,
  onAccept,
  onDecline
}) => {
  return (
    <Overlay $isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <HeaderContent>
            <IconWrapper>
              <FiAlertCircle />
            </IconWrapper>
            <TitleSection>
              <Title>Clause de Non-responsabilité et Conditions de Candidature</Title>
              <Subtitle>Veuillez lire attentivement avant de continuer</Subtitle>
            </TitleSection>
          </HeaderContent>
          <CloseButton onClick={onDecline}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <HighlightBox>
            <HighlightText>
              Merci de remplir ce formulaire de candidature pour une location. En soumettant cette candidature,
              vous acceptez les termes et conditions suivants :
            </HighlightText>
          </HighlightBox>

          <Section>
            <SectionTitle>1. Frais de Candidature</SectionTitle>
            <SectionContent>
              Des frais de candidature peuvent s'appliquer pour le traitement de votre candidature. Ces frais sont
              non remboursables et ne garantissent pas l'approbation de votre candidature.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>2. Vérification de la Situation Financière et d'Antécédents</SectionTitle>
            <SectionContent>
              En soumettant cette candidature, vous consentez à ce que nous effectuions des vérifications de votre
              situation financière, des vérifications de location antérieure et des vérifications d'antécédents,
              y compris des vérifications criminelles, le cas échéant.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>3. Protection des Données Personnelles</SectionTitle>
            <SectionContent>
              Vos informations personnelles fournies dans ce formulaire seront utilisées uniquement dans le but de
              traiter votre candidature et seront traitées conformément à notre politique de confidentialité.
              Vos informations ne seront pas partagées avec des tiers non autorisés.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>4. Informations Précises</SectionTitle>
            <SectionContent>
              Vous certifiez que toutes les informations fournies dans cette candidature sont complètes et exactes.
              La fourniture de fausses informations peut entraîner le rejet de votre candidature.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>5. Conditions du Bail</SectionTitle>
            <SectionContent>
              En soumettant cette candidature, vous comprenez que la signature d'un bail est soumise à l'approbation
              finale du propriétaire ou du gestionnaire de la propriété. La soumission de cette candidature ne
              garantit pas l'acceptation.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>6. Droits de Location Équitables</SectionTitle>
            <SectionContent>
              Nous adhérons aux lois sur les droits de location équitables et n'appliquons aucune discrimination
              illégale sur la base de la race, de la couleur, de la religion, du sexe, de l'orientation sexuelle,
              de l'âge, de l'origine nationale, de la situation matrimoniale ou de tout autre facteur protégé par la loi.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>7. Contact</SectionTitle>
            <SectionContent>
              Nous pouvons utiliser les informations de contact fournies dans ce formulaire pour vous informer du
              statut de votre candidature, planifier des visites et traiter d'autres aspects de la location.
            </SectionContent>
          </Section>

          <HighlightBox>
            <HighlightText>
              En validant ci-dessous, vous confirmez avoir lu, compris et accepté les termes et conditions de cette candidature.
            </HighlightText>
          </HighlightBox>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={onDecline}
            style={{ minWidth: '120px' }}
          >
            Je refuse
          </Button>
          <Button
            variant="primary"
            onClick={onAccept}
            style={{ minWidth: '120px' }}
          >
            J'accepte
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};
